import React, { ComponentType, RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import ParticipantIcon from '../../../../../../shared/components/participant-icon';
import { Popover } from '../../../../../../shared/components/popover';
import SvgImage from '../../../../../../shared/components/svg-image';
import { i18n } from '../../../../../../shared/helpers/i18n';
import { useTypedSelector } from '../../../../../../shared/hooks/useTypedSelector';
import { BroadcastStatus } from '../../../../../../types';
import {
    getBroadcastStarterParticipant,
    getBroadcastStatus,
    getOtherRecordingParticipants,
    isOtherUserBroadcasting,
} from '../../../../selectors';
import styles from './styles.css';

export interface WrappingButtonContainerProps {
    innerRef?: RefObject<HTMLDivElement>;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    changeNotificationVisibility?: (visible: boolean) => void;
}

const NOTIFICATION_TIMEOUT = 5000;

const truncateLongName = (name: string) => (name.length > 20 ? `${name.slice(0, 19)}...` : name);

export function withParticipantsNotifications<T>(
    WrappingButtonContainer: ComponentType<WrappingButtonContainerProps & T>
) {
    return function ButtonContainerWithParticipantsNotifications(props: T) {
        const containerRef = useRef<HTMLDivElement>(null);
        const otherRecordingParticipants = useSelector(getOtherRecordingParticipants);
        const hasOtherRecording = otherRecordingParticipants.length > 0;
        const hasOtherBroadcasting = useSelector(isOtherUserBroadcasting);
        const isMobile = useTypedSelector((state) => state.ua?.isMobile);
        const broadcastStatus = useSelector(getBroadcastStatus);
        const broadcastStarterParticipant = useSelector(getBroadcastStarterParticipant);

        const [isHover, setIsHover] = useState(false);
        const [isHidden, setIsHidden] = useState(false);

        const [showRecordNotification, setShowRecordNotification] = useState(false);
        const prevRecordingCount = useRef<number>(0);
        const notificationTimeout = useRef<number>();

        const resetNotificationTimeout = useCallback((handler: Function) => {
            window.clearTimeout(notificationTimeout.current);
            notificationTimeout.current = window.setTimeout(handler, NOTIFICATION_TIMEOUT);
        }, []);

        useEffect(() => {
            const prevValue = prevRecordingCount.current;
            const newValue = otherRecordingParticipants.length;

            prevRecordingCount.current = newValue;

            /* Мы должны показывать нотифайку только в двух случаях:
            - когда стал один записывающий;
            - когда записывающих был 1, а стало больше
             */
            if (newValue > prevValue) {
                resetNotificationTimeout(() => setShowRecordNotification(false));
                setShowRecordNotification(true);
            } else if (newValue === 0) {
                window.clearTimeout(notificationTimeout.current);
                setShowRecordNotification(false);
            }
        }, [resetNotificationTimeout, otherRecordingParticipants.length]);

        const [showBroadcastNotification, setShowBroadcastNotification] = useState(false);
        const broadcastNotificationTimeout = useRef<ReturnType<typeof setTimeout>>();

        useEffect(() => {
            if (broadcastStatus === BroadcastStatus.STARTED && hasOtherBroadcasting) {
                setShowBroadcastNotification(true);
                if (broadcastNotificationTimeout.current) {
                    clearTimeout(broadcastNotificationTimeout.current);
                }

                broadcastNotificationTimeout.current = setTimeout(() => {
                    setShowBroadcastNotification(false);
                }, NOTIFICATION_TIMEOUT);
            }
        }, [broadcastStatus, hasOtherBroadcasting]);

        const recordPopupVisible = (isHover && hasOtherRecording) || showRecordNotification;
        const broadcastPopupVisible =
            ((isHover && hasOtherBroadcasting) || showBroadcastNotification) && Boolean(broadcastStarterParticipant);

        return (
            <>
                <Popover
                    preventInteractOutside={false}
                    placement="top"
                    triggerRef={containerRef}
                    isOpen={recordPopupVisible && !isHidden}
                >
                    <div className={styles.statusNotification}>
                        <div className={styles.statusNotificationIconContainer}>
                            {otherRecordingParticipants.length === 1 ? (
                                <ParticipantIcon
                                    width={36}
                                    height={36}
                                    className={styles.statusNotificationParticipantIcon}
                                    participantId={otherRecordingParticipants[0].id}
                                />
                            ) : (
                                <SvgImage imageName="users" fill="white" style={{ width: 20, height: 20 }} />
                            )}
                        </div>
                        <div className={styles.statusNotificationText}>
                            {otherRecordingParticipants.length === 1
                                ? i18n('record', 'somebody-recording', {
                                      name:
                                          (otherRecordingParticipants?.[0]?.displayName &&
                                              truncateLongName(otherRecordingParticipants[0].displayName)) ||
                                          '',
                                  })
                                : i18n('record', 'some-participants-recording')}
                        </div>
                    </div>
                </Popover>

                <Popover
                    preventInteractOutside={false}
                    placement="top"
                    triggerRef={containerRef}
                    isOpen={broadcastPopupVisible && !isHidden}
                    offset={recordPopupVisible ? (isMobile ? 90 : 80) : undefined}
                >
                    <div className={styles.statusNotification}>
                        <div className={styles.statusNotificationIconContainer}>
                            {broadcastStarterParticipant && (
                                <ParticipantIcon
                                    width={36}
                                    height={36}
                                    participantId={broadcastStarterParticipant.id}
                                />
                            )}
                        </div>
                        <div className={styles.statusNotificationText}>
                            {broadcastStarterParticipant &&
                                broadcastStarterParticipant.displayName &&
                                i18n('statuses', 'user-broadcasting', {
                                    name: truncateLongName(broadcastStarterParticipant.displayName),
                                })}
                        </div>
                    </div>
                </Popover>

                <WrappingButtonContainer
                    innerRef={containerRef}
                    onMouseEnter={isMobile ? undefined : () => setIsHover(true)}
                    onMouseLeave={isMobile ? undefined : () => setIsHover(false)}
                    changeNotificationVisibility={(visible) => setIsHidden(!visible)}
                    {...props}
                />
            </>
        );
    };
}
